// A4 Page wrapper component for consistent report formatting
import React from 'react';

const A4Page = ({ 
  children, 
  pageNumber = 1, 
  className = "", 
  style = {}, 
  showPageNumber = true
}) => {
  return (
    <div 
      className={`bg-white shadow-lg print:shadow-none a4-page ${className}`}
      data-page-number={pageNumber}
      style={{ 
        fontFamily: 'system-ui, -apple-system, sans-serif',
        height: '297mm', // Fixed A4 height
        width: '210mm', // Fixed A4 width
        minHeight: '297mm',
        maxHeight: '297mm',
        pageBreakAfter: 'always',
        pageBreakInside: 'avoid',
        position: 'relative',
        marginBottom: '20mm',
        overflow: 'hidden',
        margin: '0 auto', // Center the page
        ...style
      }}
    >
      {/* Page content with fixed dimensions - A4 content area */}
      <div style={{
        width: '210mm', // Full A4 width
        height: '297mm', // Full A4 height
        padding: '10mm', // 10mm margins on all sides
        boxSizing: 'border-box', // Include padding in width/height
        position: 'relative',
        overflow: 'hidden',
        margin: '0 auto'
      }}>
        {children}
      </div>
    </div>
  );
};

// Enhanced Multi-page container for automatic page creation
const A4PageContainer = ({ children, className = "", contentPerPage = null }) => {
  const childrenArray = Children.toArray(children);
  
  // If no specific pagination logic, just put each child on its own page
  if (!contentPerPage) {
    return (
      <div className={className} style={{ width: '210mm', margin: '0 auto' }}>
        {childrenArray.map((child, index) => (
          <A4Page key={index} pageNumber={index + 1}>
            {child}
          </A4Page>
        ))}
      </div>
    );
  }
  
  // Split content based on contentPerPage parameter
  const pages = [];
  for (let i = 0; i < childrenArray.length; i += contentPerPage) {
    const pageItems = childrenArray.slice(i, i + contentPerPage);
    pages.push(pageItems);
  }
  
  return (
    <div className={`w-full ${className}`}>
      {pages.map((pageItems, pageIndex) => (
        <A4Page key={pageIndex} pageNumber={pageIndex + 1}>
          <div className="h-full flex flex-col">
            {pageItems}
          </div>
        </A4Page>
      ))}
    </div>
  );
};

// Enhanced A4 Multi-page component for content that needs to flow across pages
const A4MultiPageContainer = ({ children, title, className = "" }) => {
  return (
    <div className={`w-full ${className}`}>
      {/* Always create at least one page, even if empty */}
      <A4Page pageNumber={1}>
        <div className="h-full flex flex-col">
          {/* Page header */}
          {title && (
            <div className="mb-6 flex-shrink-0">
              <h1 className="text-2xl font-bold text-gray-900 pb-4 border-b-2 border-gray-200">
                {title}
              </h1>
            </div>
          )}
          
          {/* Page content - this will determine if we need more pages */}
          <div className="flex-1 overflow-hidden">
            {children}
          </div>
        </div>
      </A4Page>
    </div>
  );
};

// Hook to calculate optimal items per page based on item height
export const useA4Pagination = (itemHeight = 150, headerHeight = 100, footerHeight = 50) => {
  // A4 page dimensions in pixels (assuming 96 DPI)
  const A4_HEIGHT_PX = 1123; // 297mm = ~1123px at 96dpi
  const PADDING = 64; // 32px padding top/bottom (2rem each)
  
  const availableHeight = A4_HEIGHT_PX - headerHeight - footerHeight - PADDING;
  const itemsPerPage = Math.floor(availableHeight / itemHeight);
  
  return Math.max(1, itemsPerPage); // At least 1 item per page
};

export { A4Page, A4PageContainer, A4MultiPageContainer };
export default A4Page;